import React from 'react';
import { makeStyles } from '@material-ui/core';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useSelector } from 'react-redux';
import { useAnalytics } from '@modernhealth/web-analytics';

import { getCurrentUser } from '../selectors/userSelectors';
import PageContainer from '../components/PageContainer';
import { colors } from '../themes/colors';
import bugClientIssueReportingIcon from '../assets/svgs/bug_client_issue_reporting.svg';
import coachPoliciesProceduresIcon from '../assets/svgs/coach_policies_procedures.svg';
import faqIcon from '../assets/svgs/faq.svg';
import referralFormIcon from '../assets/svgs/referral_form.svg';
import therapistPoliciesProceduresIcon from '../assets/svgs/therapist_policies_procedures.svg';
import psychiatryAndMedicationIcon from '../assets/svgs/psychiatry-and-medication.svg';
import hpiIcon from '../assets/svgs/icon-hpi.svg';

type CardData = {
  icon: string;
  title: string;
  description: string;
  link: string;
};

const data: CardData[] = [
  {
    icon: coachPoliciesProceduresIcon,
    title: 'Coach Policies and Procedures',
    description: 'A complete guide to coaching at Modern Health.',
    link: 'https://docs.google.com/document/d/17K4DVwy0C-AcsmnrakqaDfBWEYhfLlTJO64VVLSpCX8/',
  },
  {
    icon: therapistPoliciesProceduresIcon,
    title: 'Therapist Policies and Procedures',
    description: 'A complete guide to therapy at Modern Health.',
    link: 'https://www.notion.so/modernhealththerapists/Provider-Dashboard-5939066514ba4e798c17e98df910c397',
  },
  {
    icon: faqIcon,
    title: 'Knowledge Center',
    description:
      'A resource for dashboard questions, troubleshooting, best practices, and any updates to your Modern Health experience.',
    link: 'https://modernhealthprovider.zendesk.com/hc/en-us',
  },
  {
    icon: bugClientIssueReportingIcon,
    title: 'Issue Reporting',
    description:
      'Report clients issues and/or bugs with the Modern Health product. The Modern Health team will reach back out to you over email!',
    link: 'https://modernhealthprovider.zendesk.com/hc/en-us/requests/new',
  },
  {
    icon: referralFormIcon,
    title: 'Therapy & Coaching Referral Form',
    description:
      'Refer clients to coaching or therapy at Modern Health. Obtain client permission to refer and include all information in the form.',
    link: 'https://docs.google.com/forms/d/e/1FAIpQLSdVXcT02Q7Tc8wD73r6Opq9d70pCbeEEAwZZ-FFpF4QI_XFqQ/viewform',
  },
];

const styles = makeStyles({
  wrapper: {
    padding: 60,
  },
  headerText: {
    fontSize: 18,
    fontWeight: 900,
    color: colors.black3,
  },
  resourcesList: {
    display: 'flex',
    listStyle: 'none',
    flexWrap: 'wrap',
    padding: 0,
  },
  resourceWrapper: {
    padding: 20,
    borderRadius: 20,
    margin: '0 25px 25px 0',
    width: '100%',
    maxWidth: 370,
    boxShadow: '0px 6px 14px rgba(179, 193, 210, 0.266363)',
  },
  resourceIconWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignContent: 'center',
    width: 38,
    height: 38,
    borderRadius: 19,
  },
  resourceTitle: {
    margin: '14px 0 10px',
  },
  resourceDescription: {
    fontSize: 14,
    margin: '0 0 5px',
    lineHeight: '24px',
    fontWeight: 'normal',
    color: colors.gray11,
  },
  readMore: {
    color: colors.blue6,
    textDecoration: 'none',
    fontWeight: 800,
  },
});

export const ResourcesPage: React.FC = () => {
  const analytics = useAnalytics();
  const classes = styles();
  const { showHpiClientStatusInfo } = useFlags();
  const currentUser = useSelector(getCurrentUser);
  const providerIsPsychiatrist = currentUser.isPsychiatrist;

  const cards: CardData[] = [...data];

  const onReadMoreClick = (card: CardData) => {
    analytics.trackClick(card.title);
  };

  if (providerIsPsychiatrist)
    cards.push({
      icon: psychiatryAndMedicationIcon,
      title: 'Psychiatry and Medication Management Policies and Procedures',
      description: 'A complete guide to psychiatry and medication management at Modern Health.',
      link: 'https://modernhealththerapists.notion.site/modernhealththerapists/Psychiatry-Medication-Management-Guidelines-0ae070fd8f6541b1b444990a82d438ab',
    });

  if (currentUser.isTherapist || providerIsPsychiatrist)
    cards.push({
      icon: referralFormIcon,
      title: 'Psychiatry & Medication Management Referral Form',
      description:
        'Refer clients to Psychiatry & Medication Management at Modern Health. Obtain client permission to refer and include all information in the form.',
      link: 'https://docs.google.com/forms/d/1Oj1T4WyqQ2x91AtAHioQAUukQV4B0LJeqH5g_xK-0lI/viewform',
    });

  if (showHpiClientStatusInfo) {
    cards.push({
      icon: hpiIcon,
      title: 'Health Plan Integration',
      description:
        'An option for clients to continue care with a therapist by integrating Modern Health with their health plan.',
      link: 'https://modernhealthprovider.zendesk.com/hc/en-us/sections/4411569313435-Health-Plan-Integration',
    });
  }

  return (
    <PageContainer>
      <div className={classes.wrapper}>
        <h4 className={classes.headerText}>Resources</h4>

        <ul className={classes.resourcesList}>
          {cards.map((card) => (
            <li key={card.title} className={classes.resourceWrapper}>
              {card.icon && (
                <div className={classes.resourceIconWrapper}>
                  <img src={card.icon} alt="icon" />
                </div>
              )}

              <h5 className={`${classes.headerText} ${classes.resourceTitle}`}>{card.title}</h5>

              <p className={classes.resourceDescription}>{card.description}</p>

              <a
                className={classes.readMore}
                href={card.link}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => onReadMoreClick(card)}
              >
                Read more
              </a>
            </li>
          ))}
        </ul>
      </div>
    </PageContainer>
  );
};
